import { createPortal } from "react-dom";
import { Search, Loader2 } from "lucide-react";
import type { PaletteConfig, PaletteItem } from "@/lib/palette-config";
import PaletteNeedsAttention from "./PaletteNeedsAttention";
import PaletteQuickActions from "./PaletteQuickActions";
import PaletteResults from "./PaletteResults";
import PaletteAIAnswer from "./PaletteAIAnswer";
import { useLocation } from "wouter";

interface Props {
  isOpen: boolean;
  query: string;
  results: PaletteItem[];
  selectedIndex: number;
  isLoading: boolean;
  isAILoading: boolean;
  aiAnswer: string | null;
  aiAction: { type: string; path: string; label: string } | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  config: PaletteConfig;
  onQueryChange: (query: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSelect: (item: PaletteItem) => void;
  onHover: (index: number) => void;
  onClose: () => void;
}

export default function CommandPalette({
  isOpen, query, results, selectedIndex, isLoading, isAILoading,
  aiAnswer, aiAction, inputRef, config,
  onQueryChange, onKeyDown, onSelect, onHover, onClose,
}: Props) {
  const [, navigate] = useLocation();

  if (!isOpen) return null;

  const handleNavigate = (path: string) => {
    onClose();
    navigate(path);
  };

  const hasQuery = query.trim().length > 0;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] animate-in fade-in duration-150"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        {/* Modal */}
        <div
          className="fixed left-1/2 -translate-x-1/2 top-[15%] w-[580px] max-w-[calc(100vw-2rem)] bg-gray-950 border border-white/10 rounded-2xl shadow-2xl animate-in fade-in zoom-in-[0.98] duration-150"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
            <Search className="w-5 h-5 text-white/30 flex-shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search, navigate, or ask..."
              className="flex-1 bg-transparent text-white text-sm placeholder:text-white/30 outline-none"
              autoComplete="off"
              spellCheck={false}
            />
            {isLoading && <Loader2 className="w-4 h-4 text-[#B8860B] animate-spin flex-shrink-0" />}
            <kbd className="text-[10px] text-white/30 bg-white/5 px-1.5 py-0.5 rounded border border-white/10 flex-shrink-0">
              ESC
            </kbd>
          </div>

          {/* Scrollable Content */}
          <div className="max-h-[420px] overflow-y-auto">
            {!hasQuery ? (
              <>
                <PaletteNeedsAttention onNavigate={handleNavigate} />
                <PaletteQuickActions quickActions={config.quickActions} onNavigate={handleNavigate} />
              </>
            ) : (
              <>
                <PaletteAIAnswer
                  answer={aiAnswer}
                  action={aiAction}
                  isLoading={isAILoading}
                  onNavigate={handleNavigate}
                />
                <PaletteResults
                  results={results}
                  selectedIndex={selectedIndex}
                  onSelect={onSelect}
                  onHover={onHover}
                />
                {results.length === 0 && !isLoading && !isAILoading && !aiAnswer && (
                  <div className="px-4 py-8 text-center text-white/30 text-sm">
                    No results found
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-white/10 text-[10px] text-white/20">
            <div className="hidden sm:flex gap-3">
              <span>↑↓ navigate</span>
              <span>↵ select</span>
              <span>esc close</span>
            </div>
            <span className="hidden md:block">Powered by LA Mirage AI</span>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
